import { useState } from "react";
import { Send, Sparkles, RotateCcw, Copy, Check, Lock } from "lucide-react";
import axios from "axios";
import { useInView } from "../hooks/useInView";
import WaitlistForm from "./WaitlistForm";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "https://api.relio.in";
const API = `${BACKEND_URL}/api`;

const presets = [
  {
    label: "Investor Follow-up",
    prompt: "Draft a follow-up email to Sarah Chen at Sequoia, referencing our meeting last week about our Series A. She mentioned connecting us with their portfolio company ACME Corp for a partnership.",
    context: "Sarah is a warm contact (warmth score 82). Last email was 5 days ago. She's interested in our AI product but wants to see Q3 metrics.",
  },
  {
    label: "Intro Request",
    prompt: "Draft an email asking my contact David Park to introduce me to Lisa Wang at Andreessen Horowitz. David and Lisa were colleagues at Google.",
    context: "David is a strong connection (warmth score 91). He's previously offered to make introductions. Lisa recently joined a16z's growth fund.",
  },
  {
    label: "Cold Re-engagement",
    prompt: "Draft a re-engagement email to Mark Thompson who we haven't spoken to in 3 months. He was previously interested in our seed round.",
    context: "Mark was enthusiastic 3 months ago but went silent. His fund typically invests in B2B SaaS. We've since closed 2 major clients.",
  },
];

const STORAGE_KEY = "relio_ai_demo_state";

function getStoredState() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch { /* ignore */ }
  return { draftsUsed: 0, unlocked: false };
}

function setStoredState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch { /* ignore */ }
}

export default function AIDemoSection() {
  const [sectionRef, isInView] = useInView();
  const [selectedPreset, setSelectedPreset] = useState(0);
  const [customPrompt, setCustomPrompt] = useState("");
  const [response, setResponse] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [demoState, setDemoState] = useState(getStoredState);

  const isGated = demoState.draftsUsed >= 1 && !demoState.unlocked;

  const handleUnlock = () => {
    const newState = { ...demoState, unlocked: true };
    setDemoState(newState);
    setStoredState(newState);
  };

  const handleDraft = async () => {
    if (isGated) return;

    setLoading(true);
    setResponse("");
    setCopied(false);
    
    const preset = presets[selectedPreset];
    const promptText = customPrompt || preset.prompt;
    
    try {
      const res = await axios.post(`${API}/ai/draft-email`, {
        prompt: promptText,
        context: preset.context,
      });
      setResponse(res.data.draft);

      const newState = { ...demoState, draftsUsed: demoState.draftsUsed + 1 };
      setDemoState(newState);
      setStoredState(newState);
    } catch {
      setResponse("Unable to generate draft. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(response);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: select text
      const textArea = document.createElement("textarea");
      textArea.value = response;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleReset = () => {
    setResponse("");
    setCustomPrompt("");
    setCopied(false);
  };

  return (
    <section
      id="ai-demo"
      data-testid="ai-demo-section"
      ref={sectionRef}
      className="pl-8 md:pl-16 pr-4 md:pr-8 py-16 md:py-24"
    >
      {/* Header */}
      <div
        className={`mb-12 transition-all duration-700 ${
          isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        <span className="text-xs uppercase tracking-[0.15em] font-semibold text-[#a33900] block mb-4">
          Live AI Demo
        </span>
        <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-[#1c1b1d]">
          Draft emails with
          <br />
          Claude AI.
        </h2>
        <p className="mt-4 text-base md:text-lg text-[#5a4138] max-w-lg">
          See how Relio uses your relationship context to generate perfectly-toned follow-ups.
        </p>
      </div>

      {/* Demo area */}
      <div
        className={`max-w-4xl transition-all duration-700 delay-200 ${
          isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        }`}
      >
        <div className="glass rounded-3xl p-6 md:p-8 shadow-[0_16px_40px_rgba(163,57,0,0.08)] relative">
          {/* Terminal header */}
          <div className="flex items-center gap-2 mb-6">
            <div className="w-3 h-3 rounded-full bg-[#ff5f57]" />
            <div className="w-3 h-3 rounded-full bg-[#ffbd2e]" />
            <div className="w-3 h-3 rounded-full bg-[#28c840]" />
            <span className="ml-3 text-xs text-[#5a4138] font-medium">relio-ai-composer</span>
            <div className="ml-auto flex items-center gap-1.5">
              <Sparkles size={12} className="text-[#a33900]" />
              <span className="text-[10px] text-[#a33900] font-semibold uppercase tracking-wider">Claude Sonnet 4.5</span>
            </div>
          </div>

          {/* Preset selector */}
          <div className="flex flex-wrap gap-2 mb-5">
            {presets.map((preset, i) => (
              <button
                key={i}
                data-testid={`ai-preset-${i}`}
                onClick={() => {
                  setSelectedPreset(i);
                  setCustomPrompt("");
                  setResponse("");
                }}
                className={`
                  text-xs font-medium px-3.5 py-1.5 rounded-full transition-all
                  ${selectedPreset === i
                    ? "bg-[#ff9971] text-[#772f0f]"
                    : "bg-[#f6f2f5] text-[#5a4138] hover:bg-[#f0edf0]"
                  }
                `}
              >
                {preset.label}
              </button>
            ))}
          </div>

          {/* Context display */}
          <div className="bg-[#fcf8fb] rounded-2xl p-4 mb-4">
            <span className="text-[10px] uppercase tracking-wider font-semibold text-[#a33900] block mb-2">
              Relationship Context
            </span>
            <p className="text-sm text-[#5a4138] leading-relaxed">
              {presets[selectedPreset].context}
            </p>
          </div>

          {/* Input area */}
          <div className="relative mb-5">
            <textarea
              data-testid="ai-prompt-input"
              value={customPrompt || presets[selectedPreset].prompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              placeholder="Describe what email you'd like to draft..."
              disabled={isGated}
              className="w-full bg-white rounded-2xl px-5 py-4 text-sm text-[#1c1b1d] placeholder:text-[#e2bfb2] resize-none h-24 focus:outline-none focus:ring-1 focus:ring-[#a33900] transition-all disabled:opacity-50"
              style={{ border: '1px solid rgba(226, 191, 178, 0.2)' }}
            />
          </div>

          {/* Email gate overlay */}
          {isGated ? (
            <div data-testid="ai-demo-gate" className="bg-[#fcf8fb] rounded-2xl p-6 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <Lock size={14} className="text-[#a33900]" />
                <span className="text-sm font-medium text-[#1c1b1d]">
                  Unlock unlimited AI drafts
                </span>
              </div>
              <p className="text-sm text-[#5a4138] mb-4">
                You've used your free demo draft. Enter your email to continue exploring the AI composer.
              </p>
              <WaitlistForm variant="inline" onSuccess={handleUnlock} />
            </div>
          ) : (
            <>
              {/* Actions */}
              <div className="flex items-center gap-3">
                <button
                  data-testid="ai-draft-button"
                  onClick={handleDraft}
                  disabled={loading}
                  className="cta-gradient text-white rounded-xl px-6 py-3 font-medium text-sm shadow-[0_8px_20px_rgba(204,73,0,0.25)] transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Drafting...
                    </>
                  ) : (
                    <>
                      <Send size={14} />
                      Generate Draft
                    </>
                  )}
                </button>
                {response && (
                  <>
                    <button
                      data-testid="ai-copy-button"
                      onClick={handleCopy}
                      className="bg-[#f0edf0] hover:bg-[#e2bfb2]/30 text-[#1c1b1d] rounded-xl px-4 py-3 text-sm font-medium transition-colors inline-flex items-center gap-2"
                    >
                      {copied ? <Check size={14} className="text-green-600" /> : <Copy size={14} />}
                      {copied ? "Copied" : "Copy"}
                    </button>
                    <button
                      data-testid="ai-reset-button"
                      onClick={handleReset}
                      className="bg-[#f0edf0] hover:bg-[#e2bfb2]/30 text-[#1c1b1d] rounded-xl p-3 text-sm font-medium transition-colors"
                    >
                      <RotateCcw size={14} />
                    </button>
                  </>
                )}
              </div>
            </>
          )}

          {/* Response area */}
          {(response || loading) && !isGated && (
            <div className="mt-6 bg-[#fcf8fb] rounded-2xl p-5" data-testid="ai-response-area">
              <span className="text-[10px] uppercase tracking-wider font-semibold text-[#a33900] block mb-3">
                Generated Draft
              </span>
              {loading ? (
                <div className="space-y-2">
                  <div className="h-3 bg-[#e2bfb2]/20 rounded w-3/4 animate-pulse" />
                  <div className="h-3 bg-[#e2bfb2]/20 rounded w-full animate-pulse" style={{ animationDelay: "100ms" }} />
                  <div className="h-3 bg-[#e2bfb2]/20 rounded w-5/6 animate-pulse" style={{ animationDelay: "200ms" }} />
                  <div className="h-3 bg-[#e2bfb2]/20 rounded w-2/3 animate-pulse" style={{ animationDelay: "300ms" }} />
                </div>
              ) : (
                <div className="text-sm text-[#1c1b1d] leading-relaxed whitespace-pre-wrap">
                  {response}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
